import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  useTheme,
} from '@mui/material';
import { Loader } from '../common/Loader';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import NoStatements from './components/NoStatements';

const SORT_DIRECTION = {
  asc: 'asc',
  desc: 'desc',
};

const TableHeader = ({ colName, label, sortByCol, sortAsc, onSortClick, theme }) => {
  const isSorted = sortByCol === colName;
  const sortIcon =
    isSorted && sortAsc === SORT_DIRECTION.asc ? (
      <ArrowUpwardIcon sx={{ color: theme.palette.secondary.main, fontSize: 'small' }} />
    ) : isSorted && sortAsc === SORT_DIRECTION.desc ? (
      <ArrowDownwardIcon sx={{ color: theme.palette.secondary.main, fontSize: 'small' }} />
    ) : null;

  return (
    <TableCell
      align="left"
      onClick={() => onSortClick(colName)}
      sx={{ cursor: 'pointer', userSelect: 'none' }}
      role="button"
      aria-label={`Sort by ${label}`}
    >
      <div className="font-bold hover:bg-slate-100 px-2 py-1 rounded flex items-center justify-between">
        <span>{label}</span>
        {sortIcon}
      </div>
    </TableCell>
  );
};

TableHeader.propTypes = {
  colName: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  sortByCol: PropTypes.string,
  sortAsc: PropTypes.string,
  onSortClick: PropTypes.func.isRequired,
  theme: PropTypes.object.isRequired,
};

export default function StatementsTable({
  statements,
  siteNames,
  isData,
  isLoading,
  isError,
  handleViewStatementClick,
}) {
  const [searchQuery, setSearchQuery] = useState('');
  const [sortByCol, setSortByCol] = useState(null);
  const [sortAsc, setSortAsc] = useState(null);
  const theme = useTheme();

  let filteredStatements = statements
    ?.filter((row) => {
      const searchText = searchQuery.toLowerCase();

      if (!searchText) return row;

      return (
        row.statementRef.toLowerCase().includes(searchText) ||
        siteNames[row.siteId]?.toLowerCase().includes(searchText) ||
        row.issueDate.toDate('YYYY-MM-DD').toISOString().split('T')[0].includes(searchText) ||
        (row.lastUpdated &&
          row.lastUpdated.toDate().toISOString().split('T')[0].includes(searchText))
      );
    })
    .filter((statement) => statement.verified)
    .map((row) => ({
      ...row,
      siteName: siteNames[row.siteId],
    }))
    .sort((a, b) => {
      if (!sortByCol || sortAsc === null) return 0;
      if (sortAsc === SORT_DIRECTION.asc) {
        return a[sortByCol] < b[sortByCol] ? -1 : a[sortByCol] > b[sortByCol] ? 1 : 0;
      } else {
        return a[sortByCol] > b[sortByCol] ? -1 : a[sortByCol] < b[sortByCol] ? 1 : 0;
      }
    });

  const onSortClick = (column) => {
    if (sortByCol === column) {
      setSortAsc((prev) =>
        prev === SORT_DIRECTION.asc ? SORT_DIRECTION.desc : SORT_DIRECTION.asc,
      );
    } else {
      setSortByCol(column);
      setSortAsc(SORT_DIRECTION.asc);
    }
  };

  if (isError) {
    return (
      <TableContainer
        sx={{
          marginTop: '48px',
          borderRadius: '15px',
          marginBottom: '16px',
          padding: '32px',
          display: 'flex',
          justifyContent: 'center',
        }}
        component={Paper}
      >
        <p className="text-red-700">Something went wrong. Please try again or contact support</p>
      </TableContainer>
    );
  }

  if (isData) {
    return (
      <>
        <TableContainer
          sx={{ marginTop: '48px', borderRadius: '15px', marginBottom: '16px', padding: '32px' }}
          component={Paper}
        >
          <div className="flex justify-between items-center pb-[32px]">
            <h2 className="font-bold">History</h2>
            <TextField
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              label="Search"
              variant="outlined"
              size="small"
              sx={{ width: '300px' }}
            />
          </div>

          <Table
            sx={{
              borderCollapse: 'separate',
              borderRadius: '15px',
              border: '1px solid rgb(224, 224, 224)',
            }}
            aria-label="simple table"
          >
            <TableHead>
              <TableRow key="table-header">
                <TableHeader
                  colName="statementRef"
                  label="Reference Number"
                  sortByCol={sortByCol}
                  sortAsc={sortAsc}
                  onSortClick={onSortClick}
                  theme={theme}
                />
                <TableHeader
                  colName="siteName"
                  label="Site Name"
                  sortByCol={sortByCol}
                  sortAsc={sortAsc}
                  onSortClick={onSortClick}
                  theme={theme}
                />
                <TableHeader
                  colName="issueDate"
                  label="Date Issued"
                  sortByCol={sortByCol}
                  sortAsc={sortAsc}
                  onSortClick={onSortClick}
                  theme={theme}
                />
                <TableHeader
                  colName="lastUpdated"
                  label="Updated"
                  sortByCol={sortByCol}
                  sortAsc={sortAsc}
                  onSortClick={onSortClick}
                  theme={theme}
                />
                <TableCell align="right"> </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {filteredStatements.map((row, index) => (
                <TableRow
                  key={row.statementId}
                  sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                  hover
                >
                  <TableCell align="left">{row.statementRef}</TableCell>
                  <TableCell align="left">{row.siteName}</TableCell>
                  <TableCell align="left">{`${
                    row.issueDate.toDate('YYYY-MM-DD').toISOString().split('T')[0]
                  }`}</TableCell>
                  <TableCell align="left">
                    {row.lastUpdated
                      ? `${row.lastUpdated.toDate().toISOString().split('T')[0]}`
                      : '-'}
                  </TableCell>
                  <TableCell align="right">
                    <Button
                      sx={{
                        color: theme.palette.btnActiveColor,
                        textTransform: 'none',
                        ml: 1,
                        '&.MuiButtonBase-root:hover': {
                          bgcolor: 'transparent',
                        },
                      }}
                      variant="text"
                      onClick={() => handleViewStatementClick(row)}
                    >
                      {'View Statement >'}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          { filteredStatements.length === 0 && (
          <NoStatements />
        )}
        </TableContainer>

      </>
    );
  }

  return <Loader />;
}

StatementsTable.propTypes = {
  statements: PropTypes.array.isRequired,
  siteNames: PropTypes.object.isRequired,
  isLoading: PropTypes.bool.isRequired,
  isData: PropTypes.bool.isRequired,
  isError: PropTypes.any,
  handleViewStatementClick: PropTypes.func.isRequired,
};
